/**
 * A person changes an athlete's standing in the app: the roster status on
 * the form, the status modal, a promotion. This is the in-app writer that
 * routes through the shared rule, so the transaction row, the roster label
 * and the pay strip move together the way the sync's writers do.
 *
 * The client label itself has already been written by the caller
 * (updateClient); what remains is the transaction row and the contract.
 * Anything that goes wrong here throws, so the page says so; the label is
 * already right and Data Health names the strip if it did not follow.
 */

import { listContracts, updateContract } from '../api/contracts'
import { createPlayerTransaction } from '../api/playerTransactions'
import { isNflLeague } from './leagueHelpers'
import {
  planStandingChange, payStateForRosterStatus, moveForStandingChange, isPlayingContract, MOVE_LABEL,
  type StandingPlan, type StandingPeriod,
} from '../shared/standingRules'
import type { Client } from '../types'

export type StandingWriteResult =
  | { changed: false; reason: string }
  | { changed: true; plan: StandingPlan; transactionId: string | null }

/**
 * After a client's roster_status was written by hand. `date` is the day
 * the move happened when the person gave one; otherwise today, marked
 * approximate.
 */
export async function applyStandingFromClient(
  client: Pick<Client, 'id' | 'full_name' | 'league' | 'position' | 'team_name' | 'team'>,
  from: string | null | undefined,
  to: string | null | undefined,
  opts: { date?: string | null; note?: string | null } = {},
): Promise<StandingWriteResult> {
  if (!isNflLeague(client.league)) return { changed: false, reason: 'not an NFL record' }
  if (String(from ?? '') === String(to ?? '')) return { changed: false, reason: 'roster status unchanged' }
  const payState = payStateForRosterStatus(to)
  if (!payState) return { changed: false, reason: `"${to}" has no pay meaning` }

  const contracts = (await listContracts(client.id)).filter(c => c.status === 'active' && isPlayingContract(c))
  const contract = contracts.length === 1 ? contracts[0] : null

  const move = moveForStandingChange(payStateForRosterStatus(from), payState)
  const day = (opts.date && opts.date.trim()) ? opts.date.slice(0, 10) : new Date().toISOString().slice(0, 10)
  const club = String(client.team_name ?? client.team ?? '')
  const row = await createPlayerTransaction({
    player_name: client.full_name,
    transaction_type: move,
    transaction_date: day,
    from_team: move === 'released' ? club : null,
    to_team: move === 'signed' || move === 'activated' || move === 'placed_ir' || move === 'placed_pup' ? club : null,
    position: client.position ?? null,
    league: 'NFL',
    source: 'manual',
    is_agency_client: true,
    client_id: client.id,
    notes: opts.note?.trim() || `${MOVE_LABEL[move]}: roster status set to ${to} in the app`,
  })

  const plan = planStandingChange(
    {
      client: { roster_status: to },
      contract: contract ? { id: contract.id, roster_history: (contract.roster_history ?? []) as StandingPeriod[] } : null,
      today: new Date().toISOString().slice(0, 10),
    },
    {
      payState,
      rosterStatus: null, // already written by the caller
      source: 'manual',
      date: day,
      approximate: !(opts.date && opts.date.trim()),
      transactionId: row.id,
      note: opts.note?.trim() || `Set in the app: ${MOVE_LABEL[move].toLowerCase()}`,
    },
  )
  if (plan.contract && contract) {
    await updateContract(contract.id, { roster_history: plan.contract.roster_history } as any)
  }
  return { changed: true, plan, transactionId: row.id }
}
